import type { ApiResponse, CreateProjectInput } from '@nirvik/shared';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001/v1';

/**
 * The token provider is called on every request, so the auth provider can
 * return a freshly-refreshed token. Amplify's `fetchAuthSession()` caches
 * internally and only contacts Cognito when the cached token is near
 * expiry, so calling it per request is cheap. Pass `forceRefresh: true`
 * to force Cognito round-trip (used after a 401 retry).
 */
export type TokenProvider = (opts?: { forceRefresh?: boolean }) => Promise<string | null>;

class ApiClient {
  private tokenProvider: TokenProvider | null = null;

  setTokenProvider(provider: TokenProvider | null) {
    this.tokenProvider = provider;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<ApiResponse<T>> {
    const send = async (forceRefresh: boolean): Promise<Response> => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const token = this.tokenProvider ? await this.tokenProvider({ forceRefresh }) : null;
      if (token) headers.Authorization = `Bearer ${token}`;
      return fetch(`${API_BASE}${path}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });
    };

    let response: Response;
    try {
      response = await send(false);
      if (response.status === 401 && this.tokenProvider) {
        response = await send(true);
      }
    } catch (err) {
      throw new Error(
        !this.tokenProvider
          ? 'Not authenticated. Please sign in again.'
          : 'Network error. Please check your connection and try again.',
      );
    }

    if (response.status === 401) {
      throw new Error('Your session has expired. Please sign in again.');
    }

    const data: ApiResponse<T> = await response.json();

    if (!response.ok && !data.error) {
      throw new Error(`API error: ${response.status}`);
    }

    return data;
  }

  // ── Self ──────────────────────────────────────────────────────────────
  getProfile() {
    return this.request('GET', '/me/profile');
  }

  listMyOrgs() {
    return this.request<Array<{
      orgId: string;
      name: string;
      kind: 'personal' | 'team';
      domain: string | null;
      role: 'admin' | 'member' | 'viewer';
      createdAt: string;
    }>>('GET', '/me/orgs');
  }

  // ── Orgs ──────────────────────────────────────────────────────────────
  createOrg(input: { name: string; domain?: string }) {
    return this.request<{ orgId: string; name: string; kind: 'team'; role: 'admin'; defaultPortfolioId: string }>(
      'POST', '/orgs', input,
    );
  }

  getOrg(orgId: string) {
    return this.request('GET', `/orgs/${orgId}`);
  }

  updateOrg(orgId: string, input: { name?: string; domain?: string | null; githubInstallId?: string | null }) {
    return this.request('PATCH', `/orgs/${orgId}`, input);
  }

  deleteOrg(orgId: string) {
    return this.request('DELETE', `/orgs/${orgId}`);
  }

  // Org members
  listOrgMembers(orgId: string) {
    return this.request('GET', `/orgs/${orgId}/members`);
  }

  updateOrgMember(orgId: string, userId: string, role: string) {
    return this.request('PATCH', `/orgs/${orgId}/members/${userId}`, { role });
  }

  removeOrgMember(orgId: string, userId: string) {
    return this.request('DELETE', `/orgs/${orgId}/members/${userId}`);
  }

  // Org-level invitations (admin)
  listInvitations(orgId: string) {
    return this.request('GET', `/orgs/${orgId}/invitations`);
  }

  createInvitation(
    orgId: string,
    input: { email: string; scope: 'org' | 'portfolio' | 'project'; scopeId?: string; role: 'admin' | 'member' | 'viewer' },
  ) {
    return this.request<{
      inviteId: string;
      token: string;
      expiresAt: string;
      emailSent: boolean;
      email: string;
    }>('POST', `/orgs/${orgId}/invitations`, input);
  }

  cancelInvitation(orgId: string, inviteId: string) {
    return this.request('DELETE', `/orgs/${orgId}/invitations/${inviteId}`);
  }

  resendInvitation(orgId: string, inviteId: string) {
    return this.request<{ sent: boolean; email: string }>(
      'POST',
      `/orgs/${orgId}/invitations/${inviteId}/resend`,
    );
  }

  // Token-based invitation (public, auth required to accept)
  previewInvitation(token: string) {
    return this.request('GET', `/invitations/${token}`);
  }

  acceptInvitation(token: string) {
    return this.request('POST', `/invitations/${token}/accept`);
  }

  // ── Org provider settings ─────────────────────────────────────────────
  listOrgProviders(orgId: string) {
    return this.request('GET', `/orgs/${orgId}/providers`);
  }
  updateOrgProvider(orgId: string, provider: 'anthropic' | 'bedrock' | 'vertex', input: {
    useOwnKey?: boolean;
    selectedModel?: string | null;
    key?: string | null;
  }) {
    return this.request('PATCH', `/orgs/${orgId}/providers/${provider}`, input);
  }

  // ── GitHub App integration ────────────────────────────────────────────
  /** Mint an install URL for the given org. Pop the returned `url` in a
   *  new tab; user installs the App and is redirected back to /integrations/github. */
  getGithubInstallUrl(orgId: string) {
    return this.request('GET', `/github-app/install-url?orgId=${encodeURIComponent(orgId)}`);
  }
  /** List installations linked to this org. */
  listGithubInstallations(orgId: string) {
    return this.request('GET', `/orgs/${orgId}/github/installations`);
  }
  /** Soft-unlink an installation. The user must also uninstall on GitHub. */
  deleteGithubInstallation(orgId: string, installationId: string) {
    return this.request('DELETE', `/orgs/${orgId}/github/installations/${installationId}`);
  }
  /** Repos visible to this installation (paginated server-side, capped at 500). */
  listGithubRepos(orgId: string, installationId: string) {
    return this.request('GET', `/orgs/${orgId}/github/installations/${installationId}/repos`);
  }
  /** Branches for a repo. */
  listGithubBranches(orgId: string, installationId: string, owner: string, repo: string) {
    return this.request('GET', `/orgs/${orgId}/github/installations/${installationId}/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/branches`);
  }

  // ── Portfolios ────────────────────────────────────────────────────────
  listPortfolios(orgId: string) {
    return this.request('GET', `/orgs/${orgId}/portfolios`);
  }

  createPortfolio(orgId: string, input: { name: string; description?: string }) {
    return this.request('POST', `/orgs/${orgId}/portfolios`, input);
  }

  getPortfolio(orgId: string, portfolioId: string) {
    return this.request('GET', `/orgs/${orgId}/portfolios/${portfolioId}`);
  }

  updatePortfolio(orgId: string, portfolioId: string, input: { name?: string; description?: string }) {
    return this.request('PATCH', `/orgs/${orgId}/portfolios/${portfolioId}`, input);
  }

  deletePortfolio(orgId: string, portfolioId: string) {
    return this.request('DELETE', `/orgs/${orgId}/portfolios/${portfolioId}`);
  }

  listPortfolioMembers(orgId: string, portfolioId: string) {
    return this.request('GET', `/orgs/${orgId}/portfolios/${portfolioId}/members`);
  }

  addPortfolioMember(orgId: string, portfolioId: string, input: { userId: string; role: string }) {
    return this.request('POST', `/orgs/${orgId}/portfolios/${portfolioId}/members`, input);
  }

  removePortfolioMember(orgId: string, portfolioId: string, userId: string) {
    return this.request('DELETE', `/orgs/${orgId}/portfolios/${portfolioId}/members/${userId}`);
  }

  // ── Projects ──────────────────────────────────────────────────────────
  // Org-level: list across all portfolios. Used by portfolio summary and
  // org-wide views.
  listOrgProjects(orgId: string) {
    return this.request('GET', `/orgs/${orgId}/projects`);
  }

  // Portfolio-level CRUD.
  listProjects(orgId: string, portfolioId: string) {
    return this.request('GET', `/orgs/${orgId}/portfolios/${portfolioId}/projects`);
  }

  createProject(orgId: string, portfolioId: string, input: CreateProjectInput) {
    return this.request('POST', `/orgs/${orgId}/portfolios/${portfolioId}/projects`, input);
  }

  getProject(orgId: string, portfolioId: string, projectId: string) {
    return this.request('GET', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}`);
  }

  updateProject(orgId: string, portfolioId: string, projectId: string, input: Record<string, unknown>) {
    return this.request('PATCH', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}`, input);
  }

  deleteProject(orgId: string, portfolioId: string, projectId: string) {
    return this.request('DELETE', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}`);
  }

  // ── Domain ownership verification ─────────────────────────────────────
  getDomainVerification(orgId: string, portfolioId: string, projectId: string) {
    return this.request('GET', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/domain-verification`);
  }
  checkDomainVerification(orgId: string, portfolioId: string, projectId: string, method: 'dns' | 'meta') {
    return this.request('POST', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/domain-verification/check`, { method });
  }
  regenerateDomainVerification(orgId: string, portfolioId: string, projectId: string) {
    return this.request('POST', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/domain-verification/regenerate`);
  }

  specUploadUrl(orgId: string, portfolioId: string, projectId: string, contentType: string, specKind: string) {
    return this.request<{
      url: string;
      key: string;
      expiresIn: number;
      maxBytes: number;
      method: string;
      headers: Record<string, string>;
    }>('POST', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/spec-upload-url`, { contentType, specKind });
  }

  // ── Scans ─────────────────────────────────────────────────────────────
  startScan(orgId: string, portfolioId: string, projectId: string, scanType: string = 'full') {
    return this.request('POST', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/scans`, { scanType });
  }

  listScans(orgId: string, portfolioId: string, projectId: string) {
    return this.request('GET', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/scans`);
  }

  getScan(orgId: string, portfolioId: string, projectId: string, scanId: string) {
    return this.request('GET', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/scans/${scanId}`);
  }

  cancelScan(orgId: string, portfolioId: string, projectId: string, scanId: string) {
    return this.request('POST', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/scans/${scanId}/cancel`);
  }

  // ── Findings ──────────────────────────────────────────────────────────
  listFindings(orgId: string, portfolioId: string, projectId: string, params?: { severity?: string; status?: string; runId?: string }) {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return this.request('GET', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/findings${query ? `?${query}` : ''}`);
  }

  getFinding(orgId: string, portfolioId: string, projectId: string, findingId: string) {
    return this.request('GET', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/findings/${findingId}`);
  }

  updateFinding(orgId: string, portfolioId: string, projectId: string, findingId: string, status: string) {
    return this.request('PATCH', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/findings/${findingId}`, { status });
  }

  deleteFinding(orgId: string, portfolioId: string, projectId: string, findingId: string) {
    return this.request('DELETE', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/findings/${findingId}`);
  }

  // ── Principals (per-project, multi-user pentest config) ──────────────
  listPrincipals(orgId: string, portfolioId: string, projectId: string) {
    return this.request('GET', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/principals`);
  }
  addPrincipal(orgId: string, portfolioId: string, projectId: string, body: {
    label: string; role: string;
    auth: { kind: string; headerName?: string; tokenEndpoint?: string; scopes?: string[] };
    ownedResources?: Array<{ resource: string; ids: string[] }>;
    notes?: string;
  }) {
    return this.request('POST', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/principals`, body);
  }
  updatePrincipal(orgId: string, portfolioId: string, projectId: string, principalId: string, body: Record<string, unknown>) {
    return this.request('PATCH', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/principals/${principalId}`, body);
  }
  deletePrincipal(orgId: string, portfolioId: string, projectId: string, principalId: string) {
    return this.request('DELETE', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/principals/${principalId}`);
  }
  setPrincipalCredentials(orgId: string, portfolioId: string, projectId: string, principalId: string, body: {
    value?: string; username?: string; password?: string;
    clientId?: string; clientSecret?: string;
  }) {
    return this.request('PUT', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/principals/${principalId}/credentials`, body);
  }
  clearPrincipalCredentials(orgId: string, portfolioId: string, projectId: string, principalId: string) {
    return this.request('DELETE', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/principals/${principalId}/credentials`);
  }

  explainFinding(orgId: string, portfolioId: string, projectId: string, findingId: string) {
    return this.request('POST', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/findings/${findingId}/explain`);
  }

  // ── Agents ────────────────────────────────────────────────────────────
  startAgentRun(orgId: string, portfolioId: string, projectId: string, agentType: string, config?: Record<string, unknown>) {
    return this.request('POST', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/agents/run`, { agentType, config });
  }

  startMultiAgentRun(orgId: string, portfolioId: string, projectId: string, agents: string[], config: Record<string, unknown>) {
    return this.request('POST', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/agents/run-multi`, { agents, config });
  }

  startSmartScan(orgId: string, portfolioId: string, projectId: string, config: Record<string, unknown>) {
    return this.request('POST', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/agents/run-multi`, { mode: 'smart', config });
  }

  startAllAgents(orgId: string, portfolioId: string, projectId: string, config: Record<string, unknown>) {
    return this.request('POST', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/agents/run-multi`, { mode: 'all', config });
  }

  getAgentRunReport(orgId: string, portfolioId: string, projectId: string, runId: string) {
    return this.request('GET', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/agents/runs/${runId}/report`);
  }

  deleteAgentRun(orgId: string, portfolioId: string, projectId: string, runId: string) {
    return this.request('DELETE', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/agents/runs/${runId}`);
  }

  getProjectReport(orgId: string, portfolioId: string, projectId: string) {
    return this.request('GET', `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/agents/report`);
  }

  // Org-scoped active runs (for activity indicator).
  listOrgActiveRuns(orgId: string) {
    return this.request('GET', `/orgs/${orgId}/active-runs`);
  }

  // ── Dashboard ─────────────────────────────────────────────────────────
  getDashboardSummary(orgId: string) {
    return this.request('GET', `/orgs/${orgId}/dashboard`);
  }

  getPortfolioDashboardSummary(orgId: string, portfolioId: string) {
    return this.request('GET', `/orgs/${orgId}/portfolios/${portfolioId}/dashboard`);
  }

  // ── Onboarding ────────────────────────────────────────────────────────
  getOnboardingState() {
    return this.request('GET', '/onboarding');
  }

  updateOnboardingState(input: { step?: number; complete?: boolean }) {
    return this.request('PATCH', '/onboarding', input);
  }

  // ── Admin (cross-tenant, requires Admins group) ────────────────────────
  adminDashboard() {
    return this.request('GET', '/admin/dashboard');
  }
  adminAuditLogs(cursor?: string) {
    return this.request('GET', `/admin/audit-logs${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`);
  }
  adminListOrgs(opts?: { includeArchived?: boolean }) {
    return this.request('GET', `/admin/orgs${opts?.includeArchived ? '?includeArchived=1' : ''}`);
  }
  adminGetOrg(orgId: string) {
    return this.request('GET', `/admin/orgs/${orgId}`);
  }
  adminArchiveOrg(orgId: string, reason?: string) {
    return this.request('POST', `/admin/orgs/${orgId}/archive`, { reason });
  }
  adminUnarchiveOrg(orgId: string) {
    return this.request('POST', `/admin/orgs/${orgId}/unarchive`);
  }
  adminListUsers(q?: string) {
    return this.request('GET', `/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`);
  }
  adminGetUser(userId: string) {
    return this.request('GET', `/admin/users/${userId}`);
  }
  adminSuspendUser(userId: string, reason?: string) {
    return this.request('POST', `/admin/users/${userId}/suspend`, { reason });
  }
  adminUnsuspendUser(userId: string) {
    return this.request('POST', `/admin/users/${userId}/unsuspend`);
  }
  adminUpdateUserPlan(userId: string, plan: 'free' | 'pro' | 'enterprise') {
    return this.request('PATCH', `/admin/users/${userId}/plan`, { plan });
  }
  adminGrantAdmin(userId: string) {
    return this.request('POST', `/admin/users/${userId}/grant-admin`);
  }
  adminRevokeAdmin(userId: string) {
    return this.request('POST', `/admin/users/${userId}/revoke-admin`);
  }
  adminForceCancelRun(runId: string) {
    return this.request('POST', `/admin/agent-runs/${runId}/force-cancel`);
  }

  // Provider catalog (admin)
  adminListProviders() {
    return this.request('GET', '/admin/providers');
  }
  adminUpdateProvider(provider: 'anthropic' | 'bedrock' | 'vertex', input: {
    enabled?: boolean;
    allowedModels?: string[];
    defaultModel?: string | null;
    notes?: string | null;
  }) {
    return this.request('PATCH', `/admin/providers/${provider}`, input);
  }
  adminSetProviderKey(provider: 'anthropic' | 'bedrock' | 'vertex', value: string) {
    return this.request('PUT', `/admin/providers/${provider}/key`, { value });
  }

  // ── Public SCF catalog (auth required, no org scope) ─────────────────
  listScfCatalogFrameworks() {
    return this.request('GET', '/scf/frameworks');
  }
  listScfCatalogControls(params?: { frameworks?: string[]; jurisdictions?: string[]; search?: string; limit?: number; offset?: number }) {
    const q = new URLSearchParams();
    if (params?.frameworks?.length) q.set('frameworks', params.frameworks.join(','));
    if (params?.jurisdictions?.length) q.set('jurisdictions', params.jurisdictions.join(','));
    if (params?.search) q.set('search', params.search);
    if (params?.limit != null) q.set('limit', String(params.limit));
    if (params?.offset != null) q.set('offset', String(params.offset));
    const qs = q.toString();
    return this.request('GET', `/scf/controls${qs ? `?${qs}` : ''}`);
  }
  getScfCatalogControl(scfId: string) {
    return this.request('GET', `/scf/controls/${encodeURIComponent(scfId)}`);
  }

  // ── TEP / SCF (admin + org-scoped) ────────────────────────────────────
  adminListScfVersions() {
    return this.request('GET', '/admin/scf/versions');
  }
  adminLoadScf(version?: string) {
    return this.request('POST', '/admin/scf/load', version ? { version } : {});
  }
  adminActivateScfVersion(version: string) {
    return this.request('POST', `/admin/scf/versions/${encodeURIComponent(version)}/activate`);
  }
  listTepFrameworks(orgId: string) {
    return this.request('GET', `/orgs/${orgId}/tep/frameworks`);
  }
  getTepConfig(orgId: string) {
    return this.request('GET', `/orgs/${orgId}/tep/config`);
  }
  updateTepConfig(orgId: string, input: { jurisdictions: string[]; activeFrameworks: string[]; scfVersion?: string | null }) {
    return this.request('PUT', `/orgs/${orgId}/tep/config`, input);
  }
  buildTepSnapshot(orgId: string) {
    return this.request('POST', `/orgs/${orgId}/tep/snapshots`);
  }
  listTepSnapshots(orgId: string) {
    return this.request('GET', `/orgs/${orgId}/tep/snapshots`);
  }
  getTepSnapshot(orgId: string, snapshotId: string) {
    return this.request('GET', `/orgs/${orgId}/tep/snapshots/${snapshotId}`);
  }
  getLatestTepSnapshot(orgId: string) {
    return this.request('GET', `/orgs/${orgId}/tep/snapshots/latest`);
  }
  previewTepPosture(orgId: string) {
    return this.request('GET', `/orgs/${orgId}/tep/preview`);
  }

  // ── Generic ───────────────────────────────────────────────────────────
  get<T = unknown>(path: string) {
    return this.request<T>('GET', path);
  }

  post<T = unknown>(path: string, body?: unknown) {
    return this.request<T>('POST', path, body);
  }

  put<T = unknown>(path: string, body?: unknown) {
    return this.request<T>('PUT', path, body);
  }
}

export const apiClient = new ApiClient();
